import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faHome } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb, Button, Card, Col, Form, Row, Table } from "@themesberg/react-bootstrap";
import { Link, useParams, generatePath } from "react-router-dom";

import { Routes } from "../routes";
import { apiRequest, getAuthToken } from "../utils/api";

const PURPOSES_CACHE_PREFIX = "treasurer.contributionPurposes.";

const loadCachedPurposes = (groupId) => {
  if (!groupId) return [];
  const stored = localStorage.getItem(PURPOSES_CACHE_PREFIX + groupId);
  if (!stored) return [];
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export default () => {
  const { groupId } = useParams();
  const [purposes, setPurposes] = useState(() => loadCachedPurposes(groupId));
  const [purposeForm, setPurposeForm] = useState(() => ({ name: "", description: "", treasurerNumber: "" }));
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState(() => ({ name: "", description: "", treasurerNumber: "" }));
  const [error, setError] = useState("");
  const [groupName, setGroupName] = useState("");
  const ledgerPath = generatePath(Routes.GroupTransactions.path, { groupId });

  useEffect(() => {
    const token = getAuthToken();
    if (!token || !groupId) return;
    apiRequest(`/groups/${groupId}`)
      .then((data) => {
        if (data?.group?.name) setGroupName(data.group.name);
      })
      .catch(() => {});
  }, [groupId]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token || !groupId) return;
    apiRequest(`/groups/${groupId}/contribution-purposes`)
      .then((data) => {
        if (data?.purposes) {
          setPurposes(
            data.purposes.map((p) => ({
              id: String(p.id),
              name: p.name,
              description: p.description || "",
              treasurerNumber: p.treasurerNumber || ""
            }))
          );
        }
      })
      .catch(() => {});
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    localStorage.setItem(PURPOSES_CACHE_PREFIX + groupId, JSON.stringify(purposes));
  }, [groupId, purposes]);

  const handleAddPurpose = (e) => {
    e.preventDefault();
    const name = (purposeForm?.name ?? "").trim();
    if (!name || !groupId) return;
    setError("");
    const tempId = `purp-${Date.now()}`;
    const treasurerNumber = (purposeForm?.treasurerNumber ?? "").trim() || null;
    const newPurpose = {
      id: tempId,
      name,
      description: (purposeForm?.description ?? "").trim() || "",
      treasurerNumber: treasurerNumber || ""
    };
    setPurposes((prev) => [...prev, newPurpose]);
    setPurposeForm({ name: "", description: "", treasurerNumber: "" });
    const token = getAuthToken();
    if (token && navigator.onLine) {
      apiRequest(`/groups/${groupId}/contribution-purposes`, {
        method: "POST",
        body: JSON.stringify({
          name,
          description: newPurpose.description || undefined,
          treasurerNumber: treasurerNumber || undefined
        })
      })
        .then((data) => {
          if (data?.purpose?.id) {
            setPurposes((prev) =>
              prev.map((p) =>
                p.id === tempId ? { ...p, id: String(data.purpose.id), treasurerNumber: data.purpose.treasurerNumber || "" } : p
              )
            );
          }
        })
        .catch((err) => {
          setPurposes((prev) => prev.filter((p) => p.id !== tempId));
          setError(err?.message || "Failed to add purpose");
        });
    }
  };

  const handleStartEdit = (purpose) => {
    setEditingId(purpose.id);
    setEditingValues({
      name: purpose.name || "",
      description: purpose.description || "",
      treasurerNumber: purpose.treasurerNumber || ""
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingValues({ name: "", description: "", treasurerNumber: "" });
  };

  const handleSaveEdit = (purpose) => {
    const name = (editingValues?.name ?? "").trim();
    if (!name) return;
    setError("");
    const desc = (editingValues?.description ?? "").trim();
    const treasurerNumber = (editingValues?.treasurerNumber ?? "").trim() || "";
    setPurposes((prev) =>
      prev.map((p) => (p.id === purpose.id ? { ...p, name, description: desc || "", treasurerNumber } : p))
    );
    setEditingId(null);
    setEditingValues({ name: "", description: "", treasurerNumber: "" });
    if (!String(purpose.id).startsWith("purp-") && navigator.onLine) {
      apiRequest(`/contribution-purposes/${purpose.id}`, {
        method: "PUT",
        body: JSON.stringify({ name, description: desc || undefined, treasurerNumber: treasurerNumber || undefined })
      }).catch((err) => setError(err?.message || "Failed to update"));
    }
  };

  const handleDelete = async (purpose) => {
    setError("");
    setPurposes((prev) => prev.filter((p) => p.id !== purpose.id));
    if (!String(purpose.id).startsWith("purp-") && navigator.onLine) {
      try {
        await apiRequest(`/contribution-purposes/${purpose.id}`, { method: "DELETE" });
      } catch (err) {
        setPurposes((prev) => [...prev, purpose]);
        setError(err?.message || "Cannot delete purpose (may be in use by contributions)");
      }
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item linkProps={{ as: Link, to: Routes.Presentation.path }}>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item linkProps={{ as: Link, to: ledgerPath }}>
              {groupName || "Group"}
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Contribution purposes</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Contribution purposes</h4>
          <p className="mb-0">
            Manage purposes for this group (e.g. Burial, Wedding, Dowry, Medical). Used when recording contributions.
          </p>
        </div>
        <Button
          as={Link}
          variant="outline-secondary"
          to={ledgerPath}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to group
        </Button>
      </div>

      <Row>
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Add purpose</h5>
            </Card.Header>
            <Card.Body>
              {error ? <div className="text-danger small mb-2">{error}</div> : null}
              <Form onSubmit={handleAddPurpose}>
                <Row className="g-2">
                  <Col md={3}>
                    <Form.Group className="mb-0">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Burial, Wedding, Dowry, Medical"
                        value={purposeForm?.name ?? ""}
                        onChange={(e) => {
                          const value = e.target != null ? e.target.value : "";
                          setPurposeForm((prev) => ({ ...prev, name: value }));
                        }}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-0">
                      <Form.Label>Treasurer number (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. 0712345678"
                        value={purposeForm?.treasurerNumber ?? ""}
                        onChange={(e) => {
                          const value = e.target != null ? e.target.value : "";
                          setPurposeForm((prev) => ({ ...prev, treasurerNumber: value }));
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-0">
                      <Form.Label>Description (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Optional description"
                        value={purposeForm?.description ?? ""}
                        onChange={(e) => {
                          const value = e.target != null ? e.target.value : "";
                          setPurposeForm((prev) => ({ ...prev, description: value }));
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end pb-2 pb-md-0">
                    <Button type="submit" variant="primary" disabled={!(purposeForm?.name ?? "").trim()}>
                      Add
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          <Card border="light" className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Purposes</h5>
              <span className="text-muted">
                {purposes.length} item{purposes.length !== 1 ? "s" : ""}
              </span>
            </Card.Header>
            <Card.Body className="pt-0">
              {purposes.length === 0 ? (
                <p className="text-muted mb-0">
                  No purposes yet. Add one above; they can be selected when recording income for this group.
                </p>
              ) : (
                <Table responsive className="table-centered mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th className="border-0">Name</th>
                      <th className="border-0">Treasurer number</th>
                      <th className="border-0">Description</th>
                      <th className="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purposes.map((purpose) => {
                      const isEditing = editingId === purpose.id;
                      return (
                        <tr key={purpose.id}>
                          <td className="fw-bold">
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={editingValues?.name ?? ""}
                                onChange={(e) => {
                                  const value = e.target != null ? e.target.value : "";
                                  setEditingValues((prev) => ({ ...prev, name: value }));
                                }}
                              />
                            ) : (
                              purpose.name
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                placeholder="Optional"
                                value={editingValues?.treasurerNumber ?? ""}
                                onChange={(e) => {
                                  const value = e.target != null ? e.target.value : "";
                                  setEditingValues((prev) => ({ ...prev, treasurerNumber: value }));
                                }}
                              />
                            ) : (
                              purpose.treasurerNumber || "–"
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={editingValues?.description ?? ""}
                                onChange={(e) => {
                                  const value = e.target != null ? e.target.value : "";
                                  setEditingValues((prev) => ({ ...prev, description: value }));
                                }}
                              />
                            ) : (
                              purpose.description || "–"
                            )}
                          </td>
                          <td className="text-end">
                            {isEditing ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleSaveEdit(purpose)}
                                  disabled={!(editingValues?.name ?? "").trim()}
                                >
                                  Save
                                </Button>
                                <Button size="sm" variant="outline-secondary" onClick={handleCancelEdit}>
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => handleStartEdit(purpose)}>
                                  Edit
                                </Button>
                                <Button size="sm" variant="outline-danger" onClick={() => handleDelete(purpose)}>
                                  Delete
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};
